/**
 * Desking by EnvisionDMS.com — state forms pack + HTML print merge.
 * Catalog metadata comes from the Envision Forms Library (Porter DMS Forms Module).
 * ONLY Envision library forms are allowed — no other vendor.
 */

import formsCatalog from "../data/forms-catalog.json";
import { getState } from "../data/states.js";
import { formatMoney, formatPercent } from "./calc.js";
import { openHtmlDocument } from "./docOpen.js";
import { renderArbitrationBody } from "./formsArbitration.js";
import { renderLemonLawBody } from "./formsLemonLaw.js";
import {
  renderMs78003,
  renderOfficialDmv,
  renderScForm400,
  resolveOfficialDmvType,
} from "./formsOfficialDmv.js";
import { derived, taxManualPrintNote } from "./state.js";

const DISABLED_IDS_KEY = "envision.forms.disabledIds";
const DEFAULT_PACKET_KEY = "envision.forms.defaultPacketByState";
const ASSIGNMENT_LOG_KEY = "envision.forms.assignmentRevisions";

/** Hard policy: Public Desking may only use Envision Forms Library items. */
export const ENVISION_FORMS_ONLY_POLICY =
  "ONLY the current Envision DMS / Porter DMS Forms Library may be used — no other vendor.";

const BLOCKED_VENDOR_RE =
  /reynolds|rey\s*rey|reyrey|law\s*forms?|cdk\s*global|dealertrack|routeone|tekion|vitu|cvs\s*vin/i;

export function isEnvisionLibraryForm(form = {}) {
  const source = String(form.source || "");
  const blob = [
    source,
    form.vendor,
    form.publisher,
    form.provider,
    form.name,
    form.id,
  ]
    .map((v) => String(v || ""))
    .join(" ");
  if (BLOCKED_VENDOR_RE.test(blob)) return false;
  if (!source.trim()) return true; // catalog entries without source are Envision-built
  return /^ENVISION/i.test(source) || /envision forms library/i.test(source);
}

function readJson(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    return JSON.parse(raw);
  } catch {
    return fallback;
  }
}

function writeJson(key, value) {
  localStorage.setItem(key, JSON.stringify(value));
}

export function getFormsForState(stateCode) {
  const entry = formsCatalog.states?.[stateCode];
  const forms = (entry?.forms || []).filter(isEnvisionLibraryForm);
  try {
    const raw = localStorage.getItem(DISABLED_IDS_KEY);
    const disabled = new Set(raw ? JSON.parse(raw) : []);
    if (!disabled.size) return forms;
    return forms.filter((f) => !disabled.has(f.id));
  } catch {
    return forms;
  }
}

/** Full catalog list including disabled (for super-user maintenance). */
export function getAllFormsForState(stateCode) {
  const entry = formsCatalog.states?.[stateCode];
  return (entry?.forms || []).filter(isEnvisionLibraryForm);
}

export function getDisabledFormIds() {
  try {
    const raw = localStorage.getItem(DISABLED_IDS_KEY);
    return new Set(raw ? JSON.parse(raw) : []);
  } catch {
    return new Set();
  }
}

export function setDisabledFormIds(ids, meta = {}) {
  const list = [...ids];
  const prev = [...getDisabledFormIds()];
  localStorage.setItem(DISABLED_IDS_KEY, JSON.stringify(list));
  recordAssignmentRevision({
    action: meta.action || "assign-unassign",
    stateCode: meta.stateCode || "",
    disabledIds: list,
    previousDisabledIds: prev,
    note: meta.note || "",
  });
}

/**
 * Merge unassignments for one state without wiping other states' disabled IDs.
 * Form IDs are namespaced per state (fl-*, al-*, …) in one global list.
 */
export function setDisabledFormIdsForState(stateCode, disabledForState, meta = {}) {
  const stateIds = new Set(getAllFormsForState(stateCode).map((f) => f.id));
  const kept = [...getDisabledFormIds()].filter((id) => !stateIds.has(id));
  const next = [
    ...kept,
    ...[...disabledForState].filter((id) => stateIds.has(id)),
  ];
  setDisabledFormIds(next, {
    ...meta,
    stateCode,
    action: meta.action || "save-assignments",
  });
}

export function getDefaultPacketByState() {
  const data = readJson(DEFAULT_PACKET_KEY, {});
  return data && typeof data === "object" ? data : {};
}

export function getDefaultPacketIds(stateCode) {
  const map = getDefaultPacketByState();
  const ids = map[stateCode];
  return Array.isArray(ids) ? ids : null;
}

export function setDefaultPacketIds(stateCode, ids) {
  const map = getDefaultPacketByState();
  map[stateCode] = [...ids];
  writeJson(DEFAULT_PACKET_KEY, map);
  recordAssignmentRevision({
    action: "set-default-packet",
    stateCode,
    packetIds: [...ids],
    note: `Default Start Deal packet for ${stateCode}`,
  });
}

export function clearDefaultPacketIds(stateCode) {
  const map = getDefaultPacketByState();
  delete map[stateCode];
  writeJson(DEFAULT_PACKET_KEY, map);
  recordAssignmentRevision({
    action: "clear-default-packet",
    stateCode,
    note: `Cleared default packet for ${stateCode}`,
  });
}

export function getAssignmentRevisions(limit = 40) {
  const list = readJson(ASSIGNMENT_LOG_KEY, []);
  return Array.isArray(list) ? list.slice(0, limit) : [];
}

export function recordAssignmentRevision(entry) {
  const list = getAssignmentRevisions(100);
  list.unshift({
    at: new Date().toISOString(),
    catalogVersion: formsCatalog.version,
    catalogGeneratedAt: formsCatalog.generatedAt,
    libraryUpdatedAt: formsCatalog.libraryUpdatedAt || "",
    ...entry,
  });
  writeJson(ASSIGNMENT_LOG_KEY, list.slice(0, 100));
}

export function getCatalogMeta() {
  return {
    version: formsCatalog.version,
    generatedAt: formsCatalog.generatedAt,
    libraryUpdatedAt: formsCatalog.libraryUpdatedAt || "",
    sourcePolicy:
      formsCatalog.sourcePolicy || ENVISION_FORMS_ONLY_POLICY,
    priorityTypes: formsCatalog.priorityTypes || [],
    revision: formsCatalog.revision || formsCatalog.generatedAt || "",
  };
}

export function exportFormsAssignmentSnapshot(stateCode) {
  return {
    exportedAt: new Date().toISOString(),
    policy: ENVISION_FORMS_ONLY_POLICY,
    catalog: getCatalogMeta(),
    stateCode,
    disabledIds: [...getDisabledFormIds()],
    defaultPacketIds: getDefaultPacketIds(stateCode),
    assignedForms: getAllFormsForState(stateCode).map((f) => ({
      id: f.id,
      name: f.name,
      formType: f.formType,
      formNumber: f.formNumber || "",
      source: f.source || "",
      assigned: !getDisabledFormIds().has(f.id),
    })),
    revisions: getAssignmentRevisions(25),
  };
}

function escHtml(value) {
  return String(value ?? "").replace(/[&<>"']/g, (c) =>
    ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#039;" }[c])
  );
}

function money(value) {
  return formatMoney(value);
}

const STATE_AGENCIES = {
  AL: "Alabama Department of Revenue — Motor Vehicle",
  AK: "Alaska Division of Motor Vehicles",
  AZ: "Arizona Department of Transportation — MVD",
  AR: "Arkansas Department of Finance and Administration — OMVC",
  CA: "California Department of Motor Vehicles",
  CO: "Colorado Division of Motor Vehicles",
  CT: "Connecticut Department of Motor Vehicles",
  DE: "Delaware Division of Motor Vehicles",
  DC: "District of Columbia Department of Motor Vehicles",
  FL: "Florida Highway Safety and Motor Vehicles (FLHSMV)",
  GA: "Georgia Department of Revenue — Motor Vehicle",
  HI: "Hawaii Department of Transportation",
  ID: "Idaho Transportation Department — DMV",
  IL: "Illinois Secretary of State — Vehicle Services",
  IN: "Indiana Bureau of Motor Vehicles",
  IA: "Iowa Department of Transportation — MVD",
  KS: "Kansas Department of Revenue — Vehicles",
  KY: "Kentucky Transportation Cabinet",
  LA: "Louisiana Office of Motor Vehicles",
  ME: "Maine Bureau of Motor Vehicles",
  MD: "Maryland Motor Vehicle Administration",
  MA: "Massachusetts Registry of Motor Vehicles",
  MI: "Michigan Secretary of State",
  MN: "Minnesota Driver and Vehicle Services",
  MS: "Mississippi Department of Revenue — Motor Vehicle",
  MO: "Missouri Department of Revenue",
  MT: "Montana Motor Vehicle Division",
  NE: "Nebraska Department of Motor Vehicles",
  NV: "Nevada Department of Motor Vehicles",
  NH: "New Hampshire Division of Motor Vehicles",
  NJ: "New Jersey Motor Vehicle Commission",
  NM: "New Mexico Motor Vehicle Division",
  NY: "New York Department of Motor Vehicles",
  NC: "North Carolina Division of Motor Vehicles",
  ND: "North Dakota Department of Transportation",
  OH: "Ohio Bureau of Motor Vehicles",
  OK: "Oklahoma Tax Commission — Motor Vehicle",
  OR: "Oregon Driver and Motor Vehicle Services",
  PA: "Pennsylvania Department of Transportation",
  RI: "Rhode Island Division of Motor Vehicles",
  SC: "South Carolina Department of Motor Vehicles",
  SD: "South Dakota Department of Revenue — Motor Vehicles",
  TN: "Tennessee Department of Revenue — Vehicle Services",
  TX: "Texas Department of Motor Vehicles (TxDMV)",
  UT: "Utah State Tax Commission — Motor Vehicle",
  VT: "Vermont Department of Motor Vehicles",
  VA: "Virginia Department of Motor Vehicles",
  WA: "Washington State Department of Licensing",
  WV: "West Virginia Division of Motor Vehicles",
  WI: "Wisconsin Department of Transportation — DMV",
  WY: "Wyoming Department of Transportation",
};

function stateAgency(code) {
  return STATE_AGENCIES[code] || `${code} Motor Vehicle Agency`;
}

function formStyles() {
  return `
    @page { size: letter; margin: 0.5in; }
    * { box-sizing: border-box; }
    html, body { margin: 0; padding: 0; background: #f3f6f4; color: #111; }
    body {
      font-family: "Times New Roman", Times, Georgia, serif;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
    .toolbar {
      padding: 10px 16px; background: #2f6b52; color: #fff;
      position: sticky; top: 0; z-index: 2;
      display: flex; justify-content: space-between; align-items: center; gap: 12px;
      flex-wrap: wrap;
      font-family: "DM Sans", system-ui, sans-serif; font-size: 13px;
    }
    .toolbar .hint { opacity: 0.92; font-size: 12px; }
    .toolbar .actions { display: flex; gap: 8px; flex-wrap: wrap; }
    .toolbar button {
      background: #6fbf9a; color: #143528; border: 0; padding: 8px 14px;
      border-radius: 8px; font-weight: 700; cursor: pointer;
      font-family: inherit;
    }
    .toolbar button.secondary { background: #e7f4ec; }
    .doc { max-width: 8.5in; margin: 0 auto; padding: 0.35in 0.4in 0.6in; }
    h1 { font-size: 16px; margin: 0 0 2px; text-transform: uppercase; letter-spacing: .3px; }
    h2 { font-size: 11px; margin: 8px 0 5px; text-transform: uppercase; border-bottom: 1px solid #111; padding-bottom: 2px; }
    .meta { font-size: 10px; color: #444; line-height: 1.35; }
    .header { text-align: center; border-bottom: 2px solid #111; padding-bottom: 7px; margin-bottom: 8px; }
    .brand-line { font-family: Fraunces, Georgia, "Times New Roman", serif; color: #2f6b52; font-size: 11px; letter-spacing: .03em; margin: 0 0 3px; }
    .grid2 { display: grid; grid-template-columns: 1fr 1fr; gap: 7px; }
    .grid3 { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 6px; }
    .box { border: 1px solid #111; padding: 5px 7px; min-height: 36px; font-size: 10.5px; line-height: 1.35; background: #fff; overflow-wrap: anywhere; word-break: break-word; }
    .box strong { display: block; font-size: 9px; text-transform: uppercase; letter-spacing: .3px; margin-bottom: 2px; color: #333; }
    .field { margin: 3px 0; font-size: 10.5px; }
    .field label { display: block; font-size: 9px; text-transform: uppercase; color: #555; }
    .field .val { border-bottom: 1px solid #111; min-height: 15px; padding: 1px 0; font-weight: 600; overflow-wrap: anywhere; word-break: break-word; }
    .amount { font-size: 13px; font-weight: 700; }
    .legal { font-size: 9.5px; line-height: 1.35; text-align: justify; margin: 6px 0 0; }
    .checkline { display: flex; gap: 8px; align-items: flex-start; font-size: 9.5px; margin: 3px 0; }
    .checkbox { width: 10px; height: 10px; border: 1px solid #111; display: inline-block; flex: 0 0 10px; margin-top: 2px; }
    .state-seal { font-size: 9px; color: #555; font-style: italic; margin: 2px 0 0; }
    .table { width: 100%; border-collapse: collapse; font-size: 9.5px; margin-top: 5px; table-layout: fixed; }
    .table th, .table td { border: 1px solid #111; padding: 3px 5px; text-align: left; overflow-wrap: anywhere; word-break: break-word; }
    .table th:last-child, .table td:last-child { text-align: right; white-space: nowrap; }
    .sheet {
      display: block;
      min-height: 0;
      background: #fff;
      border: 1px solid #d5e4db;
      border-radius: 6px;
      padding: 0.28in 0.32in;
      margin: 0 0 18px;
      box-shadow: 0 1px 2px rgba(20, 53, 40, 0.06);
    }
    .sheet + .sheet {
      break-before: page;
      page-break-before: always;
    }
    .sheet-main { display: block; }
    .sheet-foot {
      display: block;
      margin-top: 10px;
      padding-top: 6px;
      background: #fff;
    }
    /* Short forms only: pin signatures to bottom of one letter page (never when content overflows). */
    .sheet.is-short {
      min-height: calc(11in - 1.25in);
      display: flex;
      flex-direction: column;
    }
    .sheet.is-short .sheet-main { flex: 0 0 auto; }
    .sheet.is-short .sheet-foot { margin-top: auto; }
    .sig-block {
      padding-top: 2px;
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 8px 16px;
    }
    .sig-block .line {
      margin: 0;
      padding: 0;
      border: 0;
      min-height: 0;
    }
    .sig-block .sig-space {
      height: 28px;
    }
    .sig-block .sig-rule {
      border-top: 1px solid #111;
    }
    .sig-block .sig-label {
      font-size: 10px;
      padding-top: 4px;
      line-height: 1.3;
    }
    .doc-meta {
      margin: 10px 0 0;
      padding: 0 2px;
      font-size: 8.5px;
      color: #555;
      line-height: 1.35;
    }
    .header {
      break-after: avoid;
      page-break-after: avoid;
    }
    .packet-cover .cover-lede {
      font-family: "DM Sans", system-ui, sans-serif;
      font-size: 10.5px;
      color: #333;
      margin: 0 0 10px;
      line-height: 1.4;
    }
    .packet-cover .cover-count {
      font-family: "DM Sans", system-ui, sans-serif;
      font-size: 11px;
      font-weight: 700;
      margin: 0 0 6px;
      color: #2f6b52;
    }
    .packet-cover .cover-table {
      width: 100%;
      border-collapse: collapse;
      font-size: 10px;
      margin: 0 0 8px;
      table-layout: auto;
    }
    .packet-cover .cover-table th,
    .packet-cover .cover-table td {
      border: 1px solid #111;
      padding: 5px 7px;
      text-align: left;
      vertical-align: top;
    }
    .packet-cover .cover-table th {
      background: #e8f7ef;
      font-size: 9px;
      text-transform: uppercase;
      letter-spacing: 0.04em;
    }
    .packet-cover .cover-num {
      width: 2rem;
      text-align: center;
      font-weight: 700;
      font-variant-numeric: tabular-nums;
    }
    .packet-cover .cover-official {
      width: 22%;
      text-align: right;
      white-space: nowrap;
      font-variant-numeric: tabular-nums;
    }
    .packet-cover .cover-meta {
      font-size: 9px;
      color: #555;
      margin-top: 2px;
    }
    .packet-cover .cover-policy {
      font-size: 9px;
      line-height: 1.35;
      margin: 8px 0 0;
      color: #333;
    }
    .official-dmv-header .meta { margin-top: 4px; }
    ul.legal { margin: 4px 0 6px 1.1rem; padding: 0; font-size: 9.5px; line-height: 1.35; }
    ul.legal li { margin: 2px 0; }
    @media print {
      html, body { background: #fff !important; height: auto !important; overflow: visible !important; }
      .toolbar { display: none !important; }
      .doc {
        padding: 0 !important;
        max-width: none;
        margin: 0 !important;
      }
      .sheet {
        border: 0 !important;
        border-radius: 0;
        box-shadow: none;
        padding: 0 !important;
        margin: 0 !important;
        overflow: visible !important;
        min-height: 0 !important;
        height: auto !important;
        display: block !important;
      }
      .sheet.is-short {
        min-height: calc(11in - 1.25in) !important;
        display: flex !important;
        flex-direction: column !important;
      }
      .sheet.is-short .sheet-foot {
        margin-top: auto !important;
      }
      .sheet-main,
      .sheet-foot {
        display: block !important;
      }
      .sheet + .sheet {
        break-before: page;
        page-break-before: always;
        margin: 0 !important;
      }
      .sheet:last-child,
      .doc > .sheet:last-of-type {
        break-after: auto !important;
        page-break-after: auto !important;
      }
      /* Exactly one form sheet (cover excluded): repeat that form's signatures on every page. */
      body.single-form-sheet .sheet:not(.sheet-cover),
      body.single-form-sheet .sheet:not(.sheet-cover).is-short {
        min-height: 0 !important;
        display: block !important;
      }
      body.single-form-sheet .sheet:not(.sheet-cover) .sheet-main {
        padding-bottom: 0.95in !important;
      }
      body.single-form-sheet .sheet:not(.sheet-cover) .sheet-foot {
        position: fixed !important;
        left: 0;
        right: 0;
        bottom: 0;
        width: 100%;
        margin: 0 !important;
        padding: 6px 0 0 !important;
        background: #fff !important;
        z-index: 10;
      }
      /* Cover keeps normal / short-page pin — never the document-wide fixed foot. */
      body.single-form-sheet .sheet-cover .sheet-foot {
        position: static !important;
      }
      .sig-block .sig-space { height: 22px; }
      h1 { font-size: 14px; }
      h2 { font-size: 10px; margin: 5px 0 3px; }
      .legal { margin-top: 3px; font-size: 9px; }
      .header { margin-bottom: 5px; padding-bottom: 4px; }
      .box { min-height: 28px; padding: 4px 6px; font-size: 10px; }
      .table { font-size: 9px; }
      .table th, .table td { padding: 2px 4px; }
      .doc-meta { margin-top: 4px; font-size: 7.5px; }
    }
  `;
}

function printBootScript(autoPrint) {
  return `
<script>
(function () {
  function pinShortSheets() {
    var sheets = Array.prototype.slice.call(document.querySelectorAll(".sheet"));
    var formSheets = sheets.filter(function (s) {
      return !s.classList.contains("sheet-cover");
    });
    // Cover + one form still counts as a single form for repeating signatures.
    document.body.classList.toggle("single-form-sheet", formSheets.length === 1);
    var pageLimitPx = (11 - 1.25) * 96;
    sheets.forEach(function (sheet) {
      sheet.classList.remove("is-short");
      // Primary form uses fixed footer — skip flex short-pin on that sheet.
      if (
        document.body.classList.contains("single-form-sheet") &&
        !sheet.classList.contains("sheet-cover")
      ) {
        return;
      }
      var main = sheet.querySelector(".sheet-main");
      var foot = sheet.querySelector(".sheet-foot");
      if (!main || !foot) return;
      var used = main.scrollHeight + foot.scrollHeight;
      if (used <= pageLimitPx - 12) {
        sheet.classList.add("is-short");
      }
    });
  }
  function whenReady(fn) {
    var run = function () {
      var fontsReady = (document.fonts && document.fonts.ready)
        ? document.fonts.ready.catch(function () {})
        : Promise.resolve();
      fontsReady.then(function () {
        requestAnimationFrame(function () {
          requestAnimationFrame(function () {
            pinShortSheets();
            setTimeout(fn, 40);
          });
        });
      });
    };
    if (document.readyState === "complete") run();
    else window.addEventListener("load", run);
  }
  window.__deskPrint = function () {
    whenReady(function () { window.focus(); window.print(); });
  };
  whenReady(function () {});
  ${
    autoPrint
      ? "whenReady(function () { window.focus(); window.print(); });"
      : ""
  }
})();
</script>`;
}

function buildFormContext(state, form = {}) {
  const d = derived(state);
  const c = state.customer || {};
  const s = state.structure;
  const jur = d.jurisdiction;
  const vehicleLabel = [c.year, c.make, c.model].filter(Boolean).join(" ") || "Vehicle";
  const color = String(c.color ?? "").trim();
  const body = String(c.body ?? "").trim();
  const mileage = String(c.mileage ?? c.odometer ?? "").trim();

  return {
    form: {
      id: form.id || "",
      name: form.name || "Envision Form",
      formType: form.formType || "",
      category: form.category || "",
      description: form.description || "",
      /** Official state/DMV number from Envision Forms Library when available. */
      formNumber: form.formNumber || "",
      source: form.source || "",
    },
    dealer: {
      name: "",
      legalName: "",
      address: `${jur.name}, ${jur.code}`,
      phone: "",
      email: "",
      license: "",
      printHeader: "Desking by EnvisionDMS.com",
      state: jur.code,
    },
    location: { name: "Desking by EnvisionDMS.com", state: jur.code },
    buyer: {
      name: String(c.name || "").trim(),
      address: "",
      city: "",
      state: jur.code,
      zip: "",
      phone: c.phone || "",
      email: c.email || "",
      dl: "",
    },
    vehicle: {
      year: c.year || "",
      make: c.make || "",
      model: c.model || "",
      trim: "",
      vin: c.vin || "",
      stock: c.stock || "",
      mileage,
      color,
      body,
      label: vehicleLabel,
    },
    deal: {
      id:
        state.worksheetId ||
        `PD-${jur.code}-${Date.now().toString(36).toUpperCase()}`,
      price: Number(s.price) || 0,
      tradeAllowance: Number(s.trade) || 0,
      payoff: 0,
      cashDown: Number(d.selectedDown) || 0,
      term: Number(d.selectedTerm.months) || 0,
      rate: Number(d.selectedTerm.apr) || 0,
      payment: Number(d.selectedPayment) || 0,
      amountFinanced: Number(d.selectedAmountFinanced) || 0,
      financeCharge: Number(d.financeCharge) || 0,
      fees: Number(s.fees) || 0,
      tax: Number(s.tax) || 0,
      insGap: Number(s.insGap) || 0,
      accessories: Number(s.accessories) || 0,
      vsc: Number(s.vsc) || 0,
      firstPaymentDays: Number.isFinite(Number(s.firstPaymentDays))
        ? Number(s.firstPaymentDays)
        : 30,
      buyRate: Number(s.buyRate) || 0,
      salesperson: "",
      fiManager: "",
      products: [],
      weOwe: ["Full detail", "Second key if available", "Owner's manual"],
    },
    employee: { name: "" },
    stateCode: jur.code,
    stateName: jur.name,
    disclosureShort: jur.disclosureShort || "",
    taxManual: !!s.taxManual,
    taxManualNote: taxManualPrintNote(state),
    printedAt: new Date().toLocaleString("en-US", {
      dateStyle: "medium",
      timeStyle: "short",
    }),
  };
}

function dealerHeader(ctx, subtitle) {
  const official = String(ctx.form?.formNumber || "").trim();
  return `<div class="header">
    <p class="brand-line">Desking by EnvisionDMS.com · Envision Forms Library</p>
    <h1>${escHtml(ctx.form.name || subtitle)}</h1>
    <p class="state-seal">${escHtml(ctx.stateName)} · ${escHtml(stateAgency(ctx.stateCode))}${
      official ? ` · Official Form No. ${escHtml(official)}` : ""
    }</p>
    ${subtitle ? `<p class="meta"><strong>${escHtml(subtitle)}</strong></p>` : ""}
  </div>`;
}

function buyerBox(ctx, label = "Buyer / Purchaser") {
  const b = ctx.buyer;
  const name = String(b.name || "").trim() || "—";
  return `<div class="box"><strong>${escHtml(label)}</strong>${escHtml(name)}<br>${escHtml(
    [b.address, b.city, b.state, b.zip].filter(Boolean).join(", ")
  )}<br>Phone: ${escHtml(b.phone || "—")} · Email: ${escHtml(b.email || "—")}${
    b.dl ? `<br>Driver License: ${escHtml(b.dl)}` : ""
  }</div>`;
}

function vehicleBox(ctx, label = "Vehicle Sold") {
  const v = ctx.vehicle;
  const color = String(v.color || "").trim() || "—";
  const body = String(v.body || "").trim() || "—";
  const miles = String(v.mileage || "").trim() || "—";
  return `<div class="box"><strong>${escHtml(label)}</strong>${escHtml(v.label)}<br>Stock #: ${escHtml(
    v.stock || "—"
  )} · VIN: ${escHtml(v.vin || "________________")}<br>Color: ${escHtml(
    color
  )} · Body: ${escHtml(body)} · Odometer: ${escHtml(miles)} miles</div>`;
}

function dealerLabel(ctx) {
  const name = String(ctx.dealer?.legalName || ctx.dealer?.name || "").trim();
  return name || "the dealership";
}

function signatures(ctx) {
  const buyerName = String(ctx.buyer?.name || "").trim();
  const buyerLine = buyerName
    ? `<br><small>${escHtml(buyerName)}</small>`
    : "";
  return `<footer class="sig-block">
    <div class="line">
      <div class="sig-space" aria-hidden="true"></div>
      <div class="sig-rule"></div>
      <div class="sig-label">Buyer Signature / Date${buyerLine}</div>
    </div>
    <div class="line">
      <div class="sig-space" aria-hidden="true"></div>
      <div class="sig-rule"></div>
      <div class="sig-label">Dealership Authorized Representative / Date</div>
    </div>
  </footer>`;
}

const SIG_BLOCK_RE = /<footer class="sig-block">[\s\S]*?<\/footer>/g;

function deskingItemization(ctx) {
  const d = ctx.deal;
  return `<h2>Deal Itemization</h2>
    <table class="table">
      <tr><th>Description</th><th>Amount</th></tr>
      <tr><td>Vehicle Selling Price</td><td class="amount">${money(d.price)}</td></tr>
      <tr><td>Fees</td><td>${money(d.fees)}</td></tr>
      <tr><td>Insurance / GAP / VSI</td><td>${money(d.insGap)}</td></tr>
      <tr><td>Accessories</td><td>${money(d.accessories)}</td></tr>
      <tr><td>VSC / Products</td><td>${money(d.vsc)}</td></tr>
      <tr><td>Total Tax</td><td>${money(d.tax)}</td></tr>
      <tr><td>Down Payment</td><td>- ${money(d.cashDown)}</td></tr>
      <tr><td>Trade-In Allowance</td><td>- ${money(d.tradeAllowance)}</td></tr>
      <tr><td><strong>Amount Financed</strong></td><td class="amount">${money(d.amountFinanced)}</td></tr>
      <tr><td>Term / APR</td><td>${escHtml(d.term)} months · ${formatPercent(d.rate)}</td></tr>
      <tr><td>Estimated Monthly Payment</td><td class="amount">${money(d.payment)}</td></tr>
      <tr><td>Finance Charge</td><td>${money(d.financeCharge)}</td></tr>
      <tr><td>First Payment (days)</td><td>${escHtml(d.firstPaymentDays)}</td></tr>
    </table>`;
}

const TYPE_RENDERERS = {
  "buyers-order"(ctx) {
    return `${dealerHeader(ctx, "Motor Vehicle Purchase Agreement / Buyer's Order")}
      <div class="grid2">${buyerBox(ctx)}${vehicleBox(ctx)}</div>
      ${deskingItemization(ctx)}
      <p class="legal">This ${escHtml(ctx.stateName)} Buyer's Order is prepared from Desking by EnvisionDMS.com between ${escHtml(
        dealerLabel(ctx)
      )} and the purchaser named above. The dealership agrees to sell and the buyer agrees to purchase the vehicle described subject to ${escHtml(
        ctx.stateName
      )} regulations and title requirements administered by ${escHtml(stateAgency(ctx.stateCode))}. ${escHtml(
        ctx.disclosureShort
      )}</p>
      ${signatures(ctx)}`;
  },
  "bill-of-sale"(ctx) {
    return `${dealerHeader(ctx, "Bill of Sale")}
      <div class="grid2">${buyerBox(ctx, "Buyer")}${vehicleBox(ctx)}</div>
      ${deskingItemization(ctx)}
      <p class="legal">Seller conveys all right, title, and interest in the vehicle described above for the consideration shown. This ${escHtml(
        ctx.stateName
      )} bill of sale is issued in connection with Desking by EnvisionDMS.com worksheet ${escHtml(ctx.deal.id)}.</p>
      ${signatures(ctx)}`;
  },
  "retail-installment"(ctx) {
    const d = ctx.deal;
    const totalOfPayments = Number(d.payment) * Number(d.term || 0);
    return `${dealerHeader(ctx, "Retail Installment Sale Contract")}
      <div class="grid2">${buyerBox(ctx, "Buyer / Debtor")}${vehicleBox(ctx, "Collateral Vehicle")}</div>
      <h2>Federal Truth in Lending (Reg. Z) Style Disclosure</h2>
      <div class="grid3">
        <div class="box"><strong>Annual Percentage Rate</strong><span class="amount">${formatPercent(d.rate)}</span></div>
        <div class="box"><strong>Finance Charge</strong><span class="amount">${money(d.financeCharge)}</span></div>
        <div class="box"><strong>Amount Financed</strong><span class="amount">${money(d.amountFinanced)}</span></div>
        <div class="box"><strong>Total of Payments</strong><span class="amount">${money(totalOfPayments)}</span></div>
        <div class="box"><strong>Number of Payments</strong>${escHtml(d.term)} monthly</div>
        <div class="box"><strong>Payment Amount</strong><span class="amount">${money(d.payment)}</span></div>
      </div>
      ${deskingItemization(ctx)}
      <h2>Security Interest · Default · Repossession</h2>
      <p class="legal">Security interest is granted in the vehicle identified by VIN ${escHtml(
        ctx.vehicle.vin || "________________"
      )} and any proceeds. If Buyer defaults (including failure to pay as agreed, failure to maintain required insurance, or unauthorized transfer), Seller/Assignee may accelerate the unpaid balance and exercise remedies permitted by the Uniform Commercial Code and ${escHtml(
        ctx.stateName
      )} law, which may include repossession of the vehicle after any required notice, right to cure, or waiting period. Buyer may be responsible for reasonable repossession, storage, and sale costs as allowed by law. Buyer may redeem before sale by paying the amount due plus permitted costs. A deficiency or surplus after sale will be handled as required by applicable law.</p>
      <p class="legal">Buyer acknowledges receipt of a completed copy of this ${escHtml(
        ctx.stateName
      )} retail installment worksheet and federally required Truth in Lending disclosures (Regulation Z / 12 C.F.R. Part 1026) in a form Buyer can keep. Figures are Desking by EnvisionDMS.com estimates pending lender approval and execution of the lender's official contract package. Dealership must use the current lender/state-approved RISC when funding.</p>
      ${signatures(ctx)}`;
  },
  "truth-in-lending"(ctx) {
    return TYPE_RENDERERS["retail-installment"](ctx);
  },
  odometer(ctx) {
    return `${dealerHeader(ctx, "Federal Odometer Disclosure Statement")}
      <div class="grid2">${buyerBox(ctx, "Transferee (Buyer)")}<div class="box"><strong>Transferor (Dealership)</strong>Dealership<br>${escHtml(ctx.dealer.address)}<br>License: ${escHtml(ctx.dealer.license || "—")}</div></div>
      ${vehicleBox(ctx, "Vehicle Transferred")}
      <h2>Odometer Certification</h2>
      <p class="legal">I, the transferor, certify that to the best of my knowledge the odometer reading shown below is the <strong>actual mileage</strong> of the vehicle unless a federal exception is checked below.</p>
      <div class="box"><strong>Odometer Reading (no tenths)</strong><span class="amount">${escHtml(
        ctx.vehicle.mileage || "________"
      )}</span> miles</div>
      <div class="checkline"><span class="checkbox"></span><span>Mileage exceeds mechanical limits</span></div>
      <div class="checkline"><span class="checkbox"></span><span>Not actual mileage — WARNING: ODOMETER DISCREPANCY</span></div>
      <p class="legal">Federal law (49 U.S.C. § 32705) requires this disclosure on transfer. ${escHtml(
        ctx.stateName
      )} dealerships must retain a copy as required by law.</p>
      ${signatures(ctx)}`;
  },
  "temporary-tag"(ctx) {
    return `${dealerHeader(ctx, "Temporary Tag / Registration Permit")}
      <div class="grid2">${buyerBox(ctx)}${vehicleBox(ctx)}</div>
      <div class="grid3">
        <div class="field"><label>Permit Issue Date</label><div class="val">${escHtml(new Date().toLocaleDateString())}</div></div>
        <div class="field"><label>Expiration</label><div class="val">${escHtml(
          new Date(Date.now() + 30 * 86400000).toLocaleDateString()
        )}</div></div>
        <div class="field"><label>Issuing Location</label><div class="val">${escHtml(
          ctx.location.name || dealerLabel(ctx)
        )}</div></div>
      </div>
      <p class="legal">This ${escHtml(
        ctx.stateName
      )} temporary registration permit authorizes operation of the above vehicle pending permanent title and registration through ${escHtml(
        stateAgency(ctx.stateCode)
      )}. Buyer must carry proof of insurance as required by ${escHtml(ctx.stateName)} law.</p>
      ${signatures(ctx)}`;
  },
  "trade-disclosure"(ctx) {
    return `${dealerHeader(ctx, "Trade-In Vehicle Disclosure")}
      <div class="grid2">${buyerBox(ctx)}${vehicleBox(ctx, "Vehicle Purchased")}</div>
      <h2>Trade-In Vehicle</h2>
      <div class="box"><strong>Trade Description</strong>Year/Make/Model per appraisal<br>Allowance: <span class="amount">${money(
        ctx.deal.tradeAllowance
      )}</span> · Payoff: ${money(ctx.deal.payoff)} · Net Equity: ${money(
        ctx.deal.tradeAllowance - ctx.deal.payoff
      )}</div>
      <p class="legal">Buyer represents that the trade-in vehicle is owned free of undisclosed liens except payoff shown, has not been flood/fire damaged unless disclosed, and odometer statement is accurate per ${escHtml(
        ctx.stateName
      )} law.</p>
      ${signatures(ctx)}`;
  },
  "trade-payoff-auth"(ctx) {
    return `${dealerHeader(ctx, "Trade Payoff Authorization")}
      <div class="grid2">${buyerBox(ctx)}${vehicleBox(ctx)}</div>
      <p class="legal">Buyer authorizes the dealership to pay the trade-in lienholder the payoff amount of ${money(
        ctx.deal.payoff
      )} and apply the trade allowance of ${money(
        ctx.deal.tradeAllowance
      )} to the purchase described in worksheet ${escHtml(ctx.deal.id)}.</p>
      ${signatures(ctx)}`;
  },
  "as-is"(ctx) {
    return `${dealerHeader(ctx, "As-Is / No Dealership Warranty Disclosure")}
      <div class="grid2">${buyerBox(ctx)}${vehicleBox(ctx, "Used Vehicle — Sold As-Is")}</div>
      <h2>Warranty Disclaimer</h2>
      <p class="legal amount">THE DEALERSHIP DISCLAIMS ALL WARRANTIES, EXPRESS OR IMPLIED, INCLUDING MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE. THE VEHICLE IS SOLD <strong>AS-IS</strong>.</p>
      <p class="legal">Buyer accepts sole responsibility for repairs. ${escHtml(
        ctx.stateName
      )} used vehicle disclosure requirements apply. This disclosure is separate from any manufacturer warranty or service contract purchased.</p>
      ${signatures(ctx)}`;
  },
  "we-owe"(ctx) {
    const items = (ctx.deal.weOwe || []).map((item) => `<li>${escHtml(item)}</li>`).join("");
    return `${dealerHeader(ctx, "We-Owe / Due Bill")}
      <div class="grid2">${buyerBox(ctx)}${vehicleBox(ctx)}</div>
      <h2>Dealership Promises to Deliver</h2>
      <ul class="legal">${items || "<li>Items to be completed at delivery</li>"}</ul>
      <p class="legal">If the dealership fails to complete listed items within a reasonable time, buyer may contact the dealership. This due bill is part of worksheet ${escHtml(ctx.deal.id)}.</p>
      ${signatures(ctx)}`;
  },
  "delivery-receipt"(ctx) {
    return `${dealerHeader(ctx, "Delivery Acknowledgment / Receipt")}
      <div class="grid2">${buyerBox(ctx)}${vehicleBox(ctx)}</div>
      ${deskingItemization(ctx)}
      <p class="legal">Buyer acknowledges delivery of the vehicle and receipt of keys, owner's materials, and applicable disclosures for ${escHtml(
        ctx.stateName
      )}.</p>
      ${signatures(ctx)}`;
  },
  privacy(ctx) {
    return `${dealerHeader(ctx, "Privacy Notice — GLBA / Safeguards")}
      <div class="grid2">${buyerBox(ctx)}<div class="box"><strong>Dealership / Financial Institution</strong>Dealership<br>${escHtml(ctx.dealer.address)}</div></div>
      <p class="legal">The dealership may share nonpublic personal information with lenders, service providers, and affiliates to process your transaction, service your account, and as permitted by the Gramm-Leach-Bliley Act.</p>
      ${signatures(ctx)}`;
  },
  "power-of-attorney"(ctx) {
    return `${dealerHeader(ctx, "Limited Power of Attorney — Motor Vehicle")}
      <div class="grid2">${buyerBox(ctx, "Principal (Owner)")}<div class="box"><strong>Attorney-in-Fact (Dealership Agent)</strong>Dealership Authorized Representative</div></div>
      ${vehicleBox(ctx)}
      <p class="legal">Principal grants limited power of attorney to the dealership to sign ${escHtml(
        ctx.stateName
      )} title, registration, and lien documents for the vehicle above on principal's behalf in connection with sale/trade worksheet ${escHtml(
        ctx.deal.id
      )}.</p>
      ${signatures(ctx)}`;
  },
  "secure-power-of-attorney"(ctx) {
    return TYPE_RENDERERS["power-of-attorney"](ctx);
  },
  lease(ctx) {
    return `${dealerHeader(ctx, "Motor Vehicle Lease Agreement")}
      <div class="grid2">${buyerBox(ctx, "Lessee")}${vehicleBox(ctx, "Leased Vehicle")}</div>
      <div class="grid3">
        <div class="box"><strong>Capitalized Cost</strong>${money(ctx.deal.price)}</div>
        <div class="box"><strong>Monthly Payment</strong>${money(ctx.deal.payment)}</div>
        <div class="box"><strong>Term</strong>${escHtml(ctx.deal.term)} months</div>
      </div>
      <p class="legal">Lessee acknowledges ${escHtml(
        ctx.stateName
      )} lease disclosures including mileage allowance, excess wear charges, and purchase option if applicable.</p>
      ${signatures(ctx)}`;
  },
  "service-contract"(ctx) {
    return `${dealerHeader(ctx, "Vehicle Service Contract / VSC")}
      <div class="grid2">${buyerBox(ctx, "Contract Holder")}${vehicleBox(ctx, "Covered Vehicle")}</div>
      <p class="legal">Service contract retail amount on this worksheet: <strong>${money(
        ctx.deal.vsc
      )}</strong>. Coverage terms, exclusions, and cancellation rights are described in the administrator policy. Sale date tied to worksheet ${escHtml(
        ctx.deal.id
      )}.</p>
      ${signatures(ctx)}`;
  },
  gap(ctx) {
    return `${dealerHeader(ctx, "GAP Waiver / Debt Cancellation Agreement")}
      <div class="grid2">${buyerBox(ctx)}${vehicleBox(ctx)}</div>
      <h2>Optional GAP / Debt Cancellation Product</h2>
      <p class="legal"><strong>This Guaranteed Asset Protection (GAP) waiver / debt cancellation agreement is OPTIONAL</strong> and is not required as a condition of purchasing, leasing, or financing the vehicle. Declining GAP will not affect Buyer's ability to obtain credit on the same terms, except that Buyer remains fully responsible for any deficiency balance after an insurance total loss or unrecovered theft.</p>
      <div class="box"><strong>GAP / VSI product amount on this worksheet</strong><span class="amount">${money(
        ctx.deal.insGap
      )}</span></div>
      <p class="legal">Subject to the administrator's contract, exclusions, waiting periods, and maximum benefit limits, GAP may cancel or waive all or part of the deficiency balance remaining after primary comprehensive/collision insurance (or comparable settlement) pays for a total loss or unrecovered theft of the described vehicle. Benefits are not insurance unless issued as a separate insurance policy. Buyer should read the full GAP contract for deductibles, prior damage, overdue payments, and other exclusions before signing.</p>
      <p class="legal">Buyer may cancel within the period stated in the GAP contract (often within 30 days or as required by ${escHtml(
        ctx.stateName
      )} law) for a refund of unearned charges as provided in that contract. Amounts shown are Desking by EnvisionDMS.com worksheet estimates pending the executed GAP agreement.</p>
      ${signatures(ctx)}`;
  },
  "cash-disclosure"(ctx) {
    return `${dealerHeader(ctx, "Cash Transaction Reporting Disclosure")}
      <div class="grid2">${buyerBox(ctx)}${vehicleBox(ctx)}</div>
      <p class="legal">Federal law requires Form 8300 reporting for cash transactions over $10,000. Buyer acknowledges this disclosure for transaction amount ${money(
        ctx.deal.price
      )} on worksheet ${escHtml(ctx.deal.id)}.</p>
      ${signatures(ctx)}`;
  },
  "credit-application"(ctx) {
    return `${dealerHeader(ctx, "Credit Application")}
      <div class="grid2">${buyerBox(ctx, "Applicant")}${vehicleBox(ctx, "Vehicle to Finance")}</div>
      <h2>Employment & Credit Authorization</h2>
      <div class="grid2">
        <div class="field"><label>Employer</label><div class="val">&nbsp;</div></div>
        <div class="field"><label>Annual Income</label><div class="val">&nbsp;</div></div>
        <div class="field"><label>Housing Status</label><div class="val">&nbsp;</div></div>
        <div class="field"><label>Co-Applicant</label><div class="val">&nbsp;</div></div>
      </div>
      <p class="legal">Applicant authorizes the dealership and assigned lenders to obtain consumer reports and verify employment for worksheet ${escHtml(
        ctx.deal.id
      )} · Amount Financed ${money(ctx.deal.amountFinanced)} · Requested payment ${money(
        ctx.deal.payment
      )} / ${escHtml(ctx.deal.term)} mo.</p>
      ${signatures(ctx)}`;
  },
  "doc-fee-disclosure"(ctx) {
    return `${dealerHeader(ctx, "Documentary / Processing Fee Disclosure")}
      <div class="grid2">${buyerBox(ctx)}${vehicleBox(ctx)}</div>
      <div class="box"><strong>Documentary / Processing Fees</strong><span class="amount">${money(
        ctx.deal.fees
      )}</span></div>
      <p class="legal">Documentary and processing fees shown on this Desking by EnvisionDMS.com worksheet are disclosed prior to sale as required in ${escHtml(
        ctx.stateName
      )}. ${escHtml(ctx.disclosureShort)}</p>
      ${signatures(ctx)}`;
  },
  "lemon-law"(ctx) {
    return renderLemonLawBody(ctx, {
      dealerHeader,
      buyerBox,
      vehicleBox,
      signatures,
    });
  },
  "implied-warranty"(ctx) {
    return `${dealerHeader(ctx, "Implied Warranty (Magnuson-Moss) Disclosure")}
      <div class="grid2">${buyerBox(ctx)}${vehicleBox(ctx)}</div>
      <p class="legal">Unless a valid as-is disclaimer applies, implied warranties of merchantability may apply for a reasonable period. Any written warranty or service contract interacts with these rights as disclosed by federal and ${escHtml(
        ctx.stateName
      )} law. See also this state’s Lemon Law / Warranty Rights Notice for statute citations and agency contacts.</p>
      ${signatures(ctx)}`;
  },
  "damage-disclosure"(ctx) {
    return `${dealerHeader(ctx, "Prior Damage / Frame Damage Disclosure")}
      <div class="grid2">${buyerBox(ctx)}${vehicleBox(ctx)}</div>
      <div class="checkline"><span class="checkbox"></span><span>No known prior frame / structural damage</span></div>
      <div class="checkline"><span class="checkbox"></span><span>Prior damage disclosed (attach details)</span></div>
      <p class="legal">Dealership discloses known prior damage information for the vehicle above as required by ${escHtml(
        ctx.stateName
      )} law.</p>
      ${signatures(ctx)}`;
  },
  "spot-delivery"(ctx) {
    return `${dealerHeader(ctx, "Conditional / Spot Delivery Agreement")}
      <div class="grid2">${buyerBox(ctx)}${vehicleBox(ctx)}</div>
      ${deskingItemization(ctx)}
      <p class="legal">Delivery is conditional upon final financing approval. If financing is not approved on the terms shown, buyer agrees to return the vehicle or renegotiate per ${escHtml(
        ctx.stateName
      )} spot-delivery rules.</p>
      ${signatures(ctx)}`;
  },
  "insurance-verification"(ctx) {
    return `${dealerHeader(ctx, "Insurance Verification")}
      <div class="grid2">${buyerBox(ctx)}${vehicleBox(ctx)}</div>
      <div class="grid2">
        <div class="field"><label>Insurance Carrier</label><div class="val">&nbsp;</div></div>
        <div class="field"><label>Policy Number</label><div class="val">&nbsp;</div></div>
        <div class="field"><label>Effective Date</label><div class="val">&nbsp;</div></div>
        <div class="field"><label>Agent Phone</label><div class="val">&nbsp;</div></div>
      </div>
      <p class="legal">Buyer certifies that liability (and any required physical damage) insurance will be in force before operating the vehicle on ${escHtml(
        ctx.stateName
      )} roadways.</p>
      ${signatures(ctx)}`;
  },
  arbitration(ctx) {
    return renderArbitrationBody(ctx, {
      dealerHeader,
      buyerBox,
      vehicleBox,
      signatures,
      dealerLabel,
    });
  },
  "right-to-cancel"(ctx) {
    return `${dealerHeader(ctx, "Right to Cancel / Cooling-Off Notice")}
      <div class="grid2">${buyerBox(ctx)}${vehicleBox(ctx)}</div>
      <p class="legal">Where a cooling-off or cancellation right applies under ${escHtml(
        ctx.stateName
      )} or federal law for this transaction type, this notice advises the buyer of that right. Ask your dealership representative for the applicable deadline and method of cancellation.</p>
      ${signatures(ctx)}`;
  },
  "co-buyer-notice"(ctx) {
    return `${dealerHeader(ctx, "Co-Buyer / Co-Signer Notice")}
      <div class="grid2">${buyerBox(ctx, "Primary Buyer")}${buyerBox(ctx, "Co-Buyer / Co-Signer")}</div>
      ${vehicleBox(ctx)}
      <p class="legal">Each co-buyer/co-signer may be jointly and severally liable for the payment obligation related to Amount Financed ${money(
        ctx.deal.amountFinanced
      )} on worksheet ${escHtml(ctx.deal.id)}.</p>
      ${signatures(ctx)}`;
  },
  "emissions-inspection"(ctx) {
    return `${dealerHeader(ctx, "Emissions / Safety Inspection Disclosure")}
      <div class="grid2">${buyerBox(ctx)}${vehicleBox(ctx)}</div>
      <p class="legal">Buyer acknowledges any required ${escHtml(
        ctx.stateName
      )} emissions or safety inspection obligations administered through ${escHtml(stateAgency(ctx.stateCode))}.</p>
      ${signatures(ctx)}`;
  },
  "dot-annual-inspection"(ctx) {
    return `${dealerHeader(ctx, "DOT Annual Inspection Acknowledgment")}
      <div class="grid2">${buyerBox(ctx)}${vehicleBox(ctx)}</div>
      <p class="legal">Buyer acknowledges any applicable federal or ${escHtml(
        ctx.stateName
      )} Department of Transportation annual inspection requirements for the vehicle described above. Retain inspection records as required by law.</p>
      ${signatures(ctx)}`;
  },
  "registration-application"(ctx) {
    return renderOfficialDmv(ctx, {
      dealerHeader,
      buyerBox,
      vehicleBox,
      signatures,
      stateAgency,
      dealerLabel,
    });
  },
  "title-application"(ctx) {
    return renderOfficialDmv(ctx, {
      dealerHeader,
      buyerBox,
      vehicleBox,
      signatures,
      stateAgency,
      dealerLabel,
    });
  },
  "official-dmv"(ctx) {
    return renderOfficialDmv(ctx, {
      dealerHeader,
      buyerBox,
      vehicleBox,
      signatures,
      stateAgency,
      dealerLabel,
    });
  },
  "dmv-form-400"(ctx) {
    return renderScForm400(ctx, { signatures, dealerLabel });
  },
  "dmv-78-003"(ctx) {
    return renderMs78003(ctx, { signatures, dealerLabel });
  },
  "title-reassignment"(ctx) {
    return renderOfficialDmv(ctx, {
      dealerHeader,
      buyerBox,
      vehicleBox,
      signatures,
      stateAgency,
      dealerLabel,
    });
  },
  "vin-verification"(ctx) {
    return renderOfficialDmv(ctx, {
      dealerHeader,
      buyerBox,
      vehicleBox,
      signatures,
      stateAgency,
      dealerLabel,
    });
  },
  "marine-title-application"(ctx) {
    return renderOfficialDmv(ctx, {
      dealerHeader,
      buyerBox,
      vehicleBox,
      signatures,
      stateAgency,
      dealerLabel,
    });
  },
  "marine-trailer-title"(ctx) {
    return renderOfficialDmv(ctx, {
      dealerHeader,
      buyerBox,
      vehicleBox,
      signatures,
      stateAgency,
      dealerLabel,
    });
  },
  "marine-registration"(ctx) {
    return renderOfficialDmv(ctx, {
      dealerHeader,
      buyerBox,
      vehicleBox,
      signatures,
      stateAgency,
      dealerLabel,
    });
  },
  "sales-tax"(ctx) {
    return `${dealerHeader(ctx, "Sales / Use Tax Worksheet")}
      <div class="grid2">${buyerBox(ctx)}${vehicleBox(ctx)}</div>
      <div class="box"><strong>Total Tax (worksheet)</strong><span class="amount">${money(
        ctx.deal.tax
      )}</span></div>
      <p class="legal">${escHtml(ctx.disclosureShort)}</p>
      ${signatures(ctx)}`;
  },
};

function resolveFormType(form) {
  const type = String(form.formType || "").toLowerCase();
  const idBlob = String(form.id || "").toLowerCase();
  const id = type || idBlob;
  if (TYPE_RENDERERS[type]) return type;
  if (TYPE_RENDERERS[id]) return id;

  // Official numbered DMV / agency blanks — never fall through to Buyer's Order.
  const official = resolveOfficialDmvType(form);
  if (official && TYPE_RENDERERS[official]) return official;

  // Specific matches before broad "buy*" heuristics (avoids buyers-guide → buyers-order)
  if (id.includes("buyers-guide") || id.includes("buyer-guide") || id.includes("window-sticker"))
    return "as-is";
  // Large cash / LCTR before broad FINTRAC → privacy
  if (
    id.includes("lctr") ||
    id.includes("large-cash") ||
    id.includes("large_cash") ||
    (id.includes("cash") && id.includes("transaction"))
  ) {
    return "cash-disclosure";
  }
  if (id.includes("pipeda") || id.includes("fintrac") || id.includes("kyc") || id.includes("glba"))
    return "privacy";
  if (id.includes("buyers-order") || id.includes("purchase-agreement") || id.includes("purchase"))
    return "buyers-order";
  if (id.includes("bill-of-sale") || id.includes("billof")) return "bill-of-sale";
  if (id.includes("risc") || id.includes("installment") || id.includes("tila") || id.includes("truth"))
    return "retail-installment";
  if (id.includes("odo")) return "odometer";
  if (id.includes("temp") || id.includes("tag")) return "temporary-tag";
  if (id.includes("trade-payoff") || id.includes("payoff")) return "trade-payoff-auth";
  if (id.includes("trade")) return "trade-disclosure";
  if (id.includes("as-is") || id.includes("asis")) return "as-is";
  if (id.includes("we-owe") || id.includes("weowe") || id.includes("due-bill")) return "we-owe";
  if (id.includes("deliver")) return "delivery-receipt";
  if (id.includes("gap")) return "gap";
  if (id.includes("vsc") || id.includes("service-contract")) return "service-contract";
  if (id.includes("lease")) return "lease";
  if (id.includes("poa") || id.includes("power")) return "power-of-attorney";
  if (id.includes("credit")) return "credit-application";
  if (id.includes("privacy")) return "privacy";
  if (id.includes("doc-fee") || id.includes("documentary")) return "doc-fee-disclosure";
  if (id.includes("lemon")) return "lemon-law";
  if (id.includes("implied")) return "implied-warranty";
  if (id.includes("damage") || id.includes("frame")) return "damage-disclosure";
  if (id.includes("spot")) return "spot-delivery";
  if (id.includes("insurance")) return "insurance-verification";
  if (id.includes("arbit")) return "arbitration";
  if (id.includes("cancel") || id.includes("cooling")) return "right-to-cancel";
  if (id.includes("co-buyer") || id.includes("cosign")) return "co-buyer-notice";
  // DOT annual before broad "inspect" → emissions heuristic
  if (
    id.includes("dot-annual") ||
    id.includes("dot-inspection") ||
    (id.includes("dot") && id.includes("inspect"))
  ) {
    return "dot-annual-inspection";
  }
  if (id.includes("emission") || id.includes("inspect")) return "emissions-inspection";
  if (
    id.includes("tax") ||
    id.includes("gst") ||
    id.includes("pst") ||
    id.includes("hst")
  ) {
    return "sales-tax";
  }
  if (
    id.includes("work-order") ||
    id.includes("evhc") ||
    id.includes("service-auth") ||
    id.includes("customer-auth")
  ) {
    return "we-owe";
  }
  if (id.includes("cash") || id.includes("8300")) return "cash-disclosure";
  // Generic title/registration after official DMV resolution
  if (id.includes("title") || id.includes("registration")) return "official-dmv";
  if (String(form.category || "").toUpperCase() === "DMV") return "official-dmv";
  return "buyers-order";
}

function formMetaNote(ctx) {
  const official = String(ctx.form?.formNumber || "").trim();
  const libraryId = String(ctx.form?.id || "").trim();
  const worksheet = String(ctx.deal?.id || "").trim();
  const parts = [
    `Generated ${ctx.printedAt}`,
    official
      ? `Official Form No. ${official}`
      : libraryId
        ? `Library ID ${libraryId}`
        : null,
    worksheet ? `Worksheet ${worksheet}` : null,
    "Envision Forms Library · Desking by EnvisionDMS.com",
  ].filter(Boolean);
  return parts.join(" · ");
}

/**
 * Cover sheet for every sales-deal Forms Packet (all 50 states + DC).
 * Lists the documents the user selected to print.
 */
function buildPacketCoverSheet(forms, ctx) {
  const list = Array.isArray(forms) ? forms.filter(Boolean) : [];
  const rows = list
    .map((form, i) => {
      const name = String(form.name || "Form").trim() || "Form";
      const type = String(form.formType || form.category || "").trim();
      const official = String(form.formNumber || "").trim();
      const id = String(form.id || "").trim();
      const metaBits = [type, id && !official ? `ID ${id}` : null].filter(Boolean);
      return `<tr>
        <td class="cover-num">${i + 1}</td>
        <td>
          <strong>${escHtml(name)}</strong>
          ${
            metaBits.length
              ? `<div class="cover-meta">${escHtml(metaBits.join(" · "))}</div>`
              : ""
          }
        </td>
        <td class="cover-official">${official ? escHtml(official) : "—"}</td>
      </tr>`;
    })
    .join("");

  const body = `
    <div class="packet-cover">
      ${dealerHeader(
        {
          ...ctx,
          form: {
            ...(ctx.form || {}),
            name: "Forms Packet",
            formNumber: "",
          },
        },
        "Sales deal document cover sheet"
      )}
      <p class="cover-lede">
        This cover sheet lists every document selected for this sales deal print.
        Forms are from the <strong>Envision Forms Library</strong> for
        <strong>${escHtml(ctx.stateName || ctx.stateCode || "")}</strong>
        (${escHtml(ctx.stateCode || "")}). Generated ${escHtml(ctx.printedAt || "")}.
      </p>
      <div class="grid2">
        ${buyerBox(ctx)}
        ${vehicleBox(ctx)}
      </div>
      <h2>Documents selected to print</h2>
      <p class="cover-count">${list.length} document${list.length === 1 ? "" : "s"} in this packet</p>
      <table class="cover-table" aria-label="Selected forms in this packet">
        <thead>
          <tr>
            <th class="cover-num">#</th>
            <th>Document</th>
            <th class="cover-official">Form No.</th>
          </tr>
        </thead>
        <tbody>
          ${
            rows ||
            `<tr><td colspan="3">No documents selected.</td></tr>`
          }
        </tbody>
      </table>
      <p class="cover-policy">${escHtml(ENVISION_FORMS_ONLY_POLICY)} Dealership is responsible for using the correct, current forms for this jurisdiction.</p>
      <p class="legal">By signing below, the parties acknowledge this Forms Packet cover sheet and the listed documents that follow.</p>
    </div>
  `;

  return structureSheet(
    body,
    {
      ...ctx,
      form: {
        ...(ctx.form || {}),
        name: "Forms Packet",
        formNumber: "",
        id: "forms-packet-cover",
      },
    },
    `Forms Packet cover · ${list.length} document${list.length === 1 ? "" : "s"} · ${escHtml(
      ctx.stateCode || ""
    )} · Generated ${escHtml(ctx.printedAt || "")} · Envision Forms Library · Desking by EnvisionDMS.com`,
    { cover: true }
  );
}

function structureSheet(bodyHtml, ctx, footNote = "", opts = {}) {
  const stripped = String(bodyHtml || "").replace(SIG_BLOCK_RE, "");
  const baseNote = footNote || escHtml(formMetaNote(ctx));
  const manual = ctx.taxManualNote
    ? ` <strong>${escHtml(ctx.taxManualNote)}</strong>`
    : "";
  const note = `<p class="doc-meta">${baseNote}${manual}</p>`;
  const coverClass = opts.cover ? " sheet-cover" : "";
  // Signatures first; generated meta always prints BELOW signature lines.
  return `<section class="sheet${coverClass}">
    <div class="sheet-main">${stripped}</div>
    <div class="sheet-foot">
      ${signatures(ctx)}
      ${note}
    </div>
  </section>`;
}

function wrapDocument(title, bodyHtml, ctx, { autoPrint = false, formCount = 1 } = {}) {
  const hint = autoPrint
    ? "Preparing print dialog…"
    : "Preview ready — review each page, then click Print.";
  const countLabel =
    formCount > 1 ? `${formCount} forms` : formCount === 1 ? "1 form" : "forms";
  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8" />
  <title>${escHtml(title)} · Desking by EnvisionDMS.com</title>
  <link rel="preconnect" href="https://fonts.googleapis.com" />
  <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin />
  <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;600;700&family=Fraunces:opsz,wght@9..144,600;9..144,700&display=swap" rel="stylesheet" />
  <style>${formStyles()}</style>
</head>
<body>
  <div class="toolbar">
    <div>
      <div><strong>${escHtml(title)}</strong> · ${escHtml(ctx.stateCode)} · ${escHtml(countLabel)}</div>
      <div class="hint">${hint}</div>
    </div>
    <div class="actions">
      <button type="button" class="secondary" onclick="window.close()">Close</button>
      <button type="button" onclick="window.__deskPrint && window.__deskPrint()">Print</button>
    </div>
  </div>
  <div class="doc">${bodyHtml}</div>
  ${printBootScript(autoPrint)}
</body>
</html>`;
}

function renderBody(form, ctx) {
  const type = resolveFormType(form);
  const renderer = TYPE_RENDERERS[type] || TYPE_RENDERERS["buyers-order"];
  return structureSheet(renderer(ctx), ctx);
}

export function renderFormDocument(form, state, { autoPrint = false } = {}) {
  const ctx = buildFormContext(state, form);
  const cover = buildPacketCoverSheet([form], ctx);
  return wrapDocument(form.name || "Desking Form", `${cover}\n${renderBody(form, ctx)}`, ctx, {
    autoPrint,
    formCount: 1,
  });
}

export function openFormPrint(form, state, { autoPrint = true } = {}) {
  const html = renderFormDocument(form, state, { autoPrint });
  return openHtmlDocument(html, { title: form.name || "Desking Form" });
}

export function buildDealPacketHtml(forms, state, { autoPrint = false } = {}) {
  if (!forms?.length) return "";
  const firstCtx = buildFormContext(state, forms[0]);
  const cover = buildPacketCoverSheet(forms, firstCtx);
  const sections = forms
    .map((form) => {
      const ctx = buildFormContext(state, form);
      const type = resolveFormType(form);
      const renderer = TYPE_RENDERERS[type] || TYPE_RENDERERS["buyers-order"];
      return structureSheet(renderer(ctx), ctx);
    })
    .join("\n");

  return wrapDocument(`Forms Packet (${forms.length})`, `${cover}\n${sections}`, firstCtx, {
    autoPrint,
    formCount: forms.length,
  });
}

export function openDealPacket(forms, state, { autoPrint = false } = {}) {
  if (!forms?.length) return false;
  const html = buildDealPacketHtml(forms, state, { autoPrint });
  return openHtmlDocument(html, {
    title: `Forms Packet (${forms.length}) · Desking by EnvisionDMS.com`,
  });
}

export function defaultSelectedFormIds(stateCode) {
  const custom = getDefaultPacketIds(stateCode);
  if (custom?.length) {
    const allowed = new Set(getFormsForState(stateCode).map((f) => f.id));
    const picked = custom.filter((id) => allowed.has(id));
    if (picked.length) return picked;
  }
  const fromCatalog = getCatalogMeta().priorityTypes || [];
  const priority = new Set(
    fromCatalog.length
      ? fromCatalog
      : [
          "buyers-order",
          "bill-of-sale",
          "retail-installment",
          "truth-in-lending",
          "doc-fee-disclosure",
          "we-owe",
          "odometer",
          "gap",
          "service-contract",
        ]
  );
  return getFormsForState(stateCode)
    .filter((f) => priority.has(f.formType))
    .map((f) => f.id);
}

/** Ensure jurisdiction helper stays imported for tree-shaking clarity in catalog tools. */
export function jurisdictionLabel(code) {
  return getState(code)?.name || code;
}
